<template>
  <a-modal
    :visible="visible"
    :confirm-loading="loading"
    :footer="null"
    @cancel="handleCancel"
    :width="900"
  >
    <template #title>
      <div class="modal-title">환경 변수 일괄 등록</div>
    </template>
    
    <div class="modal-content">
      <a-tabs v-model:activeKey="activeTab">
        <a-tab-pane key="table" tab="테이블 입력">
          <div class="bulk-create-container">
            <div class="table-controls mb-3">
              <a-button type="primary" @click="addRow">행 추가</a-button>
              <a-button 
                type="primary" 
                danger 
                :disabled="!selectedRows.length"
                @click="removeSelectedRows"
              >
                선택 삭제
              </a-button>
            </div>

            <a-table
              :columns="columns"
              :data-source="configs"
              :pagination="false"
              :row-selection="{
                type: 'checkbox',
                onChange: onSelectionChange,
                selectedRowKeys: selectedRowKeys
              }"
              size="small"
            >
              <template #envType="{ record }">
                <a-select
                  v-model:value="record.envType"
                  style="width: 100%"
                  placeholder="변수 타입 선택"
                >
                  <a-select-option value="MANUFACTURER_ID">제조사 아이디</a-select-option>
                  <a-select-option value="KAKAO_TEMPLATE">카카오 알림톡 템플릿</a-select-option>
                  <a-select-option value="GENERAL">일반</a-select-option>
                </a-select>
              </template>

              <template #configKey="{ record }">
                <a-input
                  v-model:value="record.configKey"
                  placeholder="환경 변수 키를 입력하세요"
                />
              </template>

              <template #configValue="{ record }">
                <a-input
                  v-model:value="record.configValue"
                  placeholder="환경 변수 값을 입력하세요"
                />
              </template>

              <template #isSecret="{ record }">
                <a-checkbox v-model:checked="record.isSecret">
                  민감 정보
                </a-checkbox>
              </template>

              <template #desc="{ record }">
                <a-input
                  v-model:value="record.desc"
                  placeholder="환경 변수 설명을 입력하세요"
                />
              </template>

              <template #empty>
                <a-empty
                  :image="Empty.PRESENTED_IMAGE_SIMPLE"
                  description="No data"
                />
              </template>
            </a-table>
          </div>
        </a-tab-pane>

        <a-tab-pane key="json" tab="JSON 입력">
          <div class="json-input-container">
            <a-alert
              class="mb-3"
              message="사용 가능한 변수 타입(envType)"
              type="info"
              show-icon
            >
              <template #description>
                <ul class="env-type-list">
                  <li><code>MANUFACTURER_ID</code>: 제조사 아이디</li>
                  <li><code>KAKAO_TEMPLATE</code>: 카카오 알림톡 템플릿</li>
                  <li><code>GENERAL</code>: 일반 (기본값)</li>
                </ul>
              </template>
            </a-alert>

            <a-alert
              class="mb-3"
              message="JSON 형식 예시"
              type="info"
              show-icon
            >
              <template #description>
                <div class="json-example">
                  <pre><code>{{jsonExample}}</code></pre>
                  <a-button 
                    type="link" 
                    class="copy-button"
                    @click="copyExample"
                  >
                    복사
                  </a-button>
                </div>
              </template>
            </a-alert>
            <a-textarea
              v-model:value="jsonInput"
              :rows="10"
              placeholder="JSON 형식으로 입력하세요"
              @change="handleJsonChange"
            />
            <div class="json-error" v-if="jsonError">
              {{ jsonError }}
            </div>
          </div>
        </a-tab-pane>
      </a-tabs>

      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button 
          type="primary" 
          :loading="loading" 
          @click="handleSubmit"
          :disabled="isSubmitDisabled"
        >확인</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent } from 'vue';
import environmentsApis from '../api';
import { Empty } from 'ant-design-vue';

export default defineComponent({
  name: 'BulkCreateEnvConfigModal',

  props: {
    visible: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      activeTab: 'table',
      configs: [],
      selectedRows: [],
      selectedRowKeys: [],
      jsonInput: '',
      jsonError: '',
      columns: [
        {
          title: '변수 타입',
          dataIndex: 'envType',
          key: 'envType',
          width: 180,
          slots: { customRender: 'envType' }
        },
        {
          title: '환경 변수 키',
          dataIndex: 'configKey',
          key: 'configKey',
          slots: { customRender: 'configKey' }
        },
        {
          title: '환경 변수 값',
          dataIndex: 'configValue',
          key: 'configValue',
          slots: { customRender: 'configValue' }
        },
        {
          title: '민감 정보',
          dataIndex: 'isSecret',
          key: 'isSecret',
          slots: { customRender: 'isSecret' }
        },
        {
          title: '설명',
          dataIndex: 'desc',
          key: 'desc',
          slots: { customRender: 'desc' }
        }
      ],
      api: null,
      Empty,
      jsonExample: JSON.stringify([
        {
          "configKey": "EXAMPLE_KEY",
          "configValue": "example value",
          "isSecret": false,
          "envType": "GENERAL",
          "desc": "예시 환경 변수 설명"
        }
      ], null, 2),  // 2 spaces indentation
    }
  },

  created() {
    this.api = environmentsApis(this.$apiInstance, this.$store);
  },

  computed: {
    isSubmitDisabled() {
      if (this.activeTab === 'json') {
        // JSON 탭에서는 에러가 있거나 입력값이 없을 때만 비활성화
        return this.jsonError || (!this.jsonInput.trim() && this.configs.length === 0);
      }
      // 테이블 탭에서는 configs 배열이 비어있을 때만 비활성화
      return this.configs.length === 0;
    }
  },

  methods: {
    onSelectionChange(selectedRowKeys, selectedRows) {
      this.selectedRows = selectedRows;
      this.selectedRowKeys = selectedRowKeys;
    },

    addRow() {
      this.configs.push({
        key: Date.now(),
        configKey: '',
        configValue: '',
        isSecret: false,
        envType: 'GENERAL',
        desc: ''
      });
    },

    removeSelectedRows() {
      const selectedKeys = this.selectedRowKeys;
      this.configs = this.configs.filter(config => !selectedKeys.includes(config.key));
      this.selectedRows = [];
      this.selectedRowKeys = [];
    },

    handleJsonChange(e) {
      try {
        const value = e.target.value;
        this.jsonInput = value;  // 입력값 저장 추가
        
        if (!value.trim()) {
          this.jsonError = '';
          this.configs = [];  // 입력값이 없으면 configs 초기화
          return;
        }
        
        const parsed = JSON.parse(value);
        if (!Array.isArray(parsed)) {
          this.jsonError = '배열 형식이어야 합니다.';
          this.configs = [];  // 파싱 에러시 configs 초기화
          return;
        }

        const validationErrors = [];
        parsed.forEach((item, index) => {
          if (!item.configKey) validationErrors.push(`${index + 1}번째 항목: configKey가 필요합니다.`);
          if (!item.configValue) validationErrors.push(`${index + 1}번째 항목: configValue가 필요합니다.`);
        });

        if (validationErrors.length) {
          this.jsonError = validationErrors.join('\n');
          this.configs = [];  // 유효하지 않으면 configs 초기화
        } else {
          this.jsonError = '';
          // JSON이 유효하면 테이블 데이터도 업데이트
          this.configs = parsed.map((item, index) => ({
            ...item,
            key: Date.now() + index
          }));
        }
      } catch (error) {
        this.jsonError = 'JSON 형식이 올바르지 않습니다.';
        this.configs = [];  // 파싱 에러시 configs 초기화
      }
    },

    validateConfigs() {
      if (this.activeTab === 'json') {
        return this.jsonError ? [this.jsonError] : [];
      }

      const errors = [];
      this.configs.forEach((config, index) => {
        if (!config.configKey) {
          errors.push(`${index + 1}번째 행의 환경 변수 키를 입력해주세요.`);
        }
        if (!config.configValue) {
          errors.push(`${index + 1}번째 행의 환경 변수 값을 입력해주세요.`);
        }
        if (!config.envType) {
          errors.push(`${index + 1}번째 행의 변수 타입을 선택해주세요.`);
        }
      });
      return errors;
    },

    async handleSubmit() {
      const errors = this.validateConfigs();
      if (errors.length) {
        this.$message.error(errors[0]);
        return;
      }

      try {
        this.loading = true;
        
        const submitData = this.activeTab === 'json' 
          ? JSON.parse(this.jsonInput || '[]')  // 빈 문자열 처리
          : this.configs;

        if (!submitData.length) {
          this.$message.error('등록할 환경 변수가 없습니다.');
          return;
        }

        await this.api.createEnvConfigBulk(
          submitData,
          () => {
            this.$emit('success');
            this.$emit('update:visible', false);
            this.resetForm();
          },
          (error) => {
            console.error('Failed to bulk create configs:', error);
            this.$message.error('환경 변수 일괄 등록에 실패했습니다.');
            throw error;
          }
        );
      } catch (error) {
        console.error('Failed to bulk create configs:', error);
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.configs = [];
      this.selectedRows = [];
      this.selectedRowKeys = [];
      this.jsonInput = '';
      this.jsonError = '';
      this.activeTab = 'table';
    },

    handleCancel() {
      this.$emit('update:visible', false);
      this.resetForm();
    },

    async copyExample() {
      try {
        await navigator.clipboard.writeText(this.jsonExample);
        this.$message.success('예시가 클립보드에 복사되었습니다.');
      } catch (err) {
        this.$message.error('클립보드 복사에 실패했습니다.');
        console.error('Failed to copy:', err);
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

.modal-content {
  padding: 20px;
  border: none;
  background: #fff;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

:deep(.ant-modal) {
  padding-bottom: 0;
  
  .ant-modal-content {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

    .ant-modal-close {
      color: rgba(0, 0, 0, 0.45);
    }

    .ant-modal-header {
      padding: 16px 20px;
      border: none;
      background: #fff;
      margin: 0;
    }

    .ant-modal-body {
      padding: 0;
      background: #fff;
      border: none;
    }

    .ant-modal-header,
    .ant-modal-body,
    .ant-modal-footer {
      border: none;
    }
  }
}

.bulk-create-container {
  .table-controls {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }

  :deep(.ant-table-wrapper) {
    .ant-table {
      border: 1px solid #f0f0f0;
    }
  }
}

.json-input-container {
  .env-type-list {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      margin-bottom: 4px;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      code {
        background-color: #f5f5f5;
        padding: 2px 6px;
        border-radius: 4px;
        margin-right: 8px;
        font-family: 'Consolas', 'Monaco', monospace;
      }
    }
  }

  .json-example {
    position: relative;
    background: #f5f5f5;
    border-radius: 4px;
    padding: 12px;
    margin-top: 8px;

    pre {
      margin: 0;
      white-space: pre-wrap;
      word-wrap: break-word;
      
      code {
        font-family: 'Consolas', 'Monaco', monospace;
        font-size: 13px;
      }
    }

    .copy-button {
      position: absolute;
      top: 8px;
      right: 8px;
      padding: 4px 8px;
      font-size: 12px;
    }
  }

  .json-error {
    color: #ff4d4f;
    margin-top: 8px;
    white-space: pre-line;
  }
}

:deep(.ant-tabs-content) {
  margin-top: 16px;
}

.mb-3 {
  margin-bottom: 16px;
}
</style> 